import { useState } from 'react'
import { OrgView } from '@theseus/shared-ui/orgs'
import { useAuth } from '@/hooks/useAuth'
import { useUserOrgs } from '@/hooks/useUserOrgs'
import { useOrgMembers } from '@/hooks/useOrgMembers'
import { useOrgInvitations } from '@/hooks/useOrgInvitations'
import { useReceivedInvitations } from '@/hooks/useReceivedInvitations'
import {
  useCreateOrg,
  useInviteMember,
  useCancelInvitation,
  useRemoveMember,
  useAcceptInvitation,
  useDeclineInvitation,
  useLeaveOrg,
  useDeleteOrg,
} from '@/hooks/useOrgMutations'

export function OrganizationPage() {
  const { user } = useAuth()
  const { orgs, loading: orgsLoading } = useUserOrgs()
  const { invitations: receivedInvitations, loading: receivedLoading } = useReceivedInvitations()

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)

  const selectedOrg = orgs.find((o) => o.id === selectedOrgId)
  const { members, loading: membersLoading } = useOrgMembers(selectedOrgId ?? undefined)
  const { invitations: sentInvitations, loading: invitationsLoading } = useOrgInvitations(selectedOrgId ?? undefined)

  const createOrg = useCreateOrg()
  const inviteMember = useInviteMember(selectedOrgId ?? undefined)
  const cancelInvitation = useCancelInvitation()
  const removeMember = useRemoveMember()
  const acceptInvitation = useAcceptInvitation()
  const declineInvitation = useDeclineInvitation()
  const leaveOrg = useLeaveOrg()
  const deleteOrg = useDeleteOrg()

  const loading = orgsLoading || receivedLoading

  return (
    <div className="max-w-3xl">
      <h1 className="text-xl font-medium text-foreground tracking-tight mb-1">
        Organization
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        Manage your organization. Organization members share the owner's map generation quota.
      </p>
      <OrgView
        orgs={orgs}
        org={
          selectedOrg
            ? {
              id: selectedOrg.id,
              name: selectedOrg.name,
              ownerId: '',
              memberCount: members.length,
              createdAt: '',
            }
            : null
        }
        members={membersLoading ? [] : members}
        membersLoading={membersLoading}
        sentInvitations={invitationsLoading ? [] : sentInvitations}
        receivedInvitations={receivedInvitations}
        currentUserRole={selectedOrg?.role ?? null}
        currentUserEmail={user?.email ?? ''}
        loading={loading}
        onCreateOrg={async (name) => { await createOrg.mutateAsync(name) }}
        onInviteMember={async (email) => { await inviteMember.mutateAsync(email) }}
        onCancelInvitation={async (id) => { await cancelInvitation.mutateAsync(id) }}
        onRemoveMember={async (id) => { await removeMember.mutateAsync(id) }}
        onAcceptInvitation={async (id) => { await acceptInvitation.mutateAsync(id) }}
        onDeclineInvitation={async (id) => { await declineInvitation.mutateAsync(id) }}
        selectedOrgId={selectedOrgId}
        onSelectOrg={setSelectedOrgId}
        onLeaveOrg={async (orgId) => {
          await leaveOrg.mutateAsync(orgId)
          if (selectedOrgId === orgId) setSelectedOrgId(null)
        }}
        onDeleteOrg={async (orgId) => {
          await deleteOrg.mutateAsync(orgId)
          if (selectedOrgId === orgId) setSelectedOrgId(null)
        }}
      />
    </div>
  )
}
